import { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin, ExternalLink, Map as MapIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { Field, inputCls } from "./common";
import MapEmbed from "./MapEmbed";

const NUMBER_FIRST = ["US", "GB", "CA", "AU", "NZ", "IE", "FR", "BE", "LU", "IN", "ZA", "IL", "SG", "MY", "PH"];
let keyPromise;
export const getKey = () => (keyPromise ||= api.get("/config/maps").then((r) => r.data.api_key).catch(() => null));

export function parse(components, formatted, id) {
  const get = (type, short = false) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (short ? c.shortText : c.longText) : "";
  };
  const cc = get("country", true);
  const route = get("route");
  const num = get("street_number");
  const street = route ? (NUMBER_FIRST.includes(cc) ? [num, route] : [route, num]).filter(Boolean).join(" ") : get("premise") || get("sublocality");
  const prov = cc === "IT" ? get("administrative_area_level_2", true) : get("administrative_area_level_1");
  return {
    street,
    route,
    number: num,
    city: get("locality") || get("postal_town") || get("administrative_area_level_3") || get("sublocality_level_1") || get("administrative_area_level_2"),
    province: prov,
    postal_code: get("postal_code"),
    country: get("country"),
    formatted,
    place_id: id,
  };
}

function MapPicker({ onPick }) {
  const [addr, setAddr] = useState(null);
  const [q, setQ] = useState("");
  const where = addr?.formatted || "Amalfi, SA, Italia";

  return (
    <div className="space-y-3">
      <StreetSuggest value={q} onType={setQ} onSelect={(x) => { setAddr(x); setQ(x.formatted || x.street); }} placeholder="Cerca via, città o Paese (anche estero)" testid="map-search-input" />
      <div className="h-[45vh] rounded-xl overflow-hidden border border-slate-200" data-testid="map-canvas">
        <MapEmbed className="w-full h-full" q={where} zoom={addr ? 17 : 13} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600" data-testid="map-selected-address">{addr ? addr.formatted : "Cerca l'indirizzo e sceglilo dall'elenco"}</p>
        <button type="button" disabled={!addr} onClick={() => onPick(addr)} className="px-4 py-2.5 rounded-lg bg-[#C25E00] text-white text-sm font-semibold disabled:opacity-40" data-testid="map-use-address-btn">Usa questo indirizzo</button>
      </div>
    </div>
  );
}

function MapDialog({ apiKey, onClose, onPick }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="map-dialog">
        <DialogHeader><DialogTitle>Google Maps</DialogTitle><DialogDescription>Trova l'indirizzo del cliente: verrà inserito nel contratto.</DialogDescription></DialogHeader>
        <APIProvider apiKey={apiKey} language="it" version="weekly"><MapPicker onPick={onPick} /></APIProvider>
      </DialogContent>
    </Dialog>
  );
}

export function StreetSuggest({ value, onType, onSelect, onLeave, placeholder = "Scrivi l'indirizzo: Google Maps lo completa", testid = "address-street", className = inputCls, lang = "it", bias = null }) {
  const places = useMapsLibrary("places");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const token = useRef(null);
  const typed = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!places?.AutocompleteSuggestion || !typed.current || isLink(value) || (value || "").trim().length < 3) { setItems([]); setOpen(false); return undefined; }
    const t = setTimeout(async () => {
      token.current ||= new places.AutocompleteSessionToken();
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: value, sessionToken: token.current, language: lang, ...(bias ? { locationBias: bias } : {}) });
        setItems(suggestions.filter((s) => s.placePrediction).slice(0, 6));
        setOpen(document.activeElement === inputRef.current);
      } catch { setItems([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [value, places, lang, bias]);

  const pick = async (s) => {
    setOpen(false);
    typed.current = false;
    const place = s?.placePrediction?.toPlace();
    if (!place) return;
    try { await place.fetchFields({ fields: ["addressComponents", "formattedAddress", "id", "location"] }); } catch { return; }
    token.current = null;
    const ll = place.location ? { lat: place.location.lat(), lng: place.location.lng() } : {};
    onSelect({ ...parse(place.addressComponents || [], place.formattedAddress, place.id), name: s.placePrediction.mainText?.text || "", ...ll });
  };

  return (
    <div className="relative">
      <input ref={inputRef} className={className} value={value || ""} placeholder={placeholder} autoComplete="off"
        onChange={(e) => { typed.current = true; onType(e.target.value); }} onFocus={() => items.length && setOpen(true)} onBlur={() => { setTimeout(() => setOpen(false), 200); onLeave?.(); }} data-testid={testid} />
      {open && items.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="address-suggestions">
          {items.map((s, i) => (
            <button type="button" key={s.placePrediction.placeId} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} className="w-full text-left px-3 py-2.5 hover:bg-orange-50 flex items-start gap-2 border-b border-slate-100 last:border-0" data-testid={`address-suggestion-${i}`}>
              <MapPin size={15} className="mt-0.5 text-[#C25E00] shrink-0" />
              <span className="text-sm"><span className="font-semibold text-[#0F2042]">{s.placePrediction.mainText?.text}</span> <span className="text-slate-500">{s.placePrediction.secondaryText?.text}</span></span>
            </button>
          ))}
          <p className="text-[10px] text-slate-400 text-right px-3 py-1">Google Maps</p>
        </div>
      )}
    </div>
  );
}

export const openMaps = (e, url) => {
  e.preventDefault();
  const w = window.open(url, "_blank");
  if (w) w.opener = null; else window.location.href = url;
};
export const isLink = (v) => /^\s*https?:\/\//i.test(v || "");
export const addrLine = (a) => a?.formatted || [a?.street, [a?.postal_code, a?.city].filter(Boolean).join(" "), a?.province, a?.country].filter(Boolean).join(", ");
export const isComplete = (a) => ["street", "city", "country"].every((k) => (a?.[k] || "").trim());
const fromGeo = (r) => parse((r.address_components || []).map((c) => ({ longText: c.long_name, shortText: c.short_name, types: c.types })), r.formatted_address, r.place_id);
const withTimeout = (p, ms = 6000) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);
const POSTAL = ["street_address", "premise", "subpremise"];
const near = (loc, g) => loc && Math.hypot(loc.lat() - g.lat, (loc.lng() - g.lng) * Math.cos((g.lat * Math.PI) / 180)) * 111000 < 40;
const M = {
  it: { ph: "Indirizzo completo o link Google Maps", busy: "Leggo la posizione…", found: "Indirizzo trovato da Google Maps ✓", gps: (la, ln) => `Posizione GPS ${la.toFixed(5)}, ${ln.toFixed(5)}: indirizzo postale non disponibile`, noAddr: "Luogo senza indirizzo postale completo: il link viene salvato", unresolved: "Non riesco a leggere questo link: verrà salvato così com'è", open: "Apri link Google Maps", resIncomplete: "Per la residenza serve un indirizzo postale completo (via, città, Paese): un link o una posizione GPS non bastano" },
  en: { ph: "Full address or Google Maps link", busy: "Reading the location…", found: "Address found by Google Maps ✓", gps: (la, ln) => `GPS position ${la.toFixed(5)}, ${ln.toFixed(5)}: no postal address available`, noAddr: "Place without a full postal address: the link is saved", unresolved: "This link could not be read: it will be saved as it is", open: "Open Google Maps link", resIncomplete: "A full postal address (street, city, country) is required for residence: a link or GPS position is not enough" },
};

export function SmartAddress({ value, onChange, lang = "it", className = inputCls, testid = "address-smart", residence = false, resolvePath = "/maps/resolve-link" }) {
  const places = useMapsLibrary("places");
  const geocoding = useMapsLibrary("geocoding");
  const a = value || {};
  const T = M[lang] || M.en;
  const [text, setText] = useState(addrLine(a) || a.maps_url || "");
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  const resolveLink = async (url) => {
    setSt({ k: "busy", m: T.busy });
    const base = { maps_url: url, source: "link" };
    let r;
    try { r = (await api.post(resolvePath, { url })).data; } catch { onChange({ ...base, formatted: null }); setSt({ k: "warn", m: T.unresolved }); return; }
    const geo = r.lat != null ? { lat: r.lat, lng: r.lng } : {};
    let found = null;
    try {
      if (r.query && places) {
        const { places: p } = await withTimeout(places.Place.searchByText({ textQuery: r.query, fields: ["addressComponents", "formattedAddress", "id", "location"], maxResultCount: 1, ...(r.lat != null ? { locationBias: geo } : {}) }));
        if (p?.[0]) { found = parse(p[0].addressComponents || [], p[0].formattedAddress, p[0].id); if (geo.lat == null && p[0].location) { geo.lat = p[0].location.lat(); geo.lng = p[0].location.lng(); } }
      } else if (r.lat != null) {
        if (geocoding) {
          try {
            const { results } = await withTimeout(new geocoding.Geocoder().geocode({ location: geo }));
            const best = results?.find((x) => x.types.some((t) => POSTAL.includes(t)));
            if (best) found = fromGeo(best);
          } catch { found = null; }
        }
        if (!found && places) {
          const { places: p } = await withTimeout(places.Place.searchByText({ textQuery: `${geo.lat},${geo.lng}`, fields: ["addressComponents", "formattedAddress", "id", "location", "types"], maxResultCount: 1 }));
          if (p?.[0] && near(p[0].location, geo) && (p[0].types || []).some((t) => POSTAL.includes(t))) found = parse(p[0].addressComponents || [], p[0].formattedAddress, p[0].id);
        }
      }
    } catch { found = null; }
    if (found?.street || found?.city) { onChange({ ...found, ...base, ...geo }); setText(found.formatted || addrLine(found)); setSt({ k: "ok", m: T.found }); }
    else { onChange({ ...base, ...geo, formatted: null }); setSt({ k: "warn", m: geo.lat != null ? T.gps(geo.lat, geo.lng) : T.noAddr }); }
  };
  const onType = (v) => {
    setText(v); clearTimeout(timer.current); setSt(null);
    if (isLink(v)) { timer.current = setTimeout(() => resolveLink(v.trim()), 400); return; }
    onChange({ formatted: v || null, source: "manual" });
  };
  const onLeave = async () => {
    if (a.source !== "manual" || isComplete(a) || (text || "").trim().length < 6 || !places) return;
    try {
      const { places: p } = await withTimeout(places.Place.searchByText({ textQuery: text, fields: ["addressComponents", "formattedAddress", "id", "types"], maxResultCount: 1 }));
      const best = p?.[0];
      if (best && (best.types || []).some((t) => [...POSTAL, "route"].includes(t))) { const x = parse(best.addressComponents || [], best.formattedAddress, best.id); onChange({ ...x, source: "manual" }); setText(x.formatted); setSt({ k: "ok", m: T.found }); }
    } catch { setSt(null); }
  };
  return (
    <div className="space-y-1.5" data-testid={`${testid}-block`}>
      <StreetSuggest value={text} onType={onType} onLeave={onLeave} onSelect={(x) => { onChange({ ...x, source: "places" }); setText(x.formatted); setSt({ k: "ok", m: T.found }); }} placeholder={T.ph} testid={testid} className={className} lang={lang} />
      {st && <p className={`text-xs ${st.k === "warn" ? "text-amber-700" : "text-slate-600"}`} data-testid={`${testid}-status`}>{st.m}</p>}
      {a.maps_url && <a href={a.maps_url} target="_blank" rel="noreferrer" onClick={(e) => openMaps(e, a.maps_url)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C25E00] hover:underline" data-testid={`${testid}-link`}><ExternalLink size={13} /> {T.open}</a>}
      {residence && (text || a.maps_url) && !isComplete(a) && st?.k !== "busy" && <p className="text-xs text-rose-700" data-testid={`${testid}-incomplete`}>{T.resIncomplete}</p>}
    </div>
  );
}

export default function AddressAutocomplete({ value, onChange }) {
  const a = value || {};
  const [key, setKey] = useState(undefined);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  useEffect(() => { getKey().then(setKey); }, []);
  const street = key ? (
    <APIProvider apiKey={key} language="it" version="weekly">
      <SmartAddress key={mapKey} value={a} onChange={onChange} testid="address-street" />
    </APIProvider>
  ) : <input className={inputCls} value={a.formatted || addrLine(a)} placeholder="Indirizzo completo" onChange={(e) => onChange({ formatted: e.target.value || null, source: "manual" })} data-testid="address-street" />;

  return (
    <div className="space-y-3" data-testid="address-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-700">Indirizzo di residenza</p>
        {key && <button type="button" onClick={() => setMapOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F2042] text-white text-sm font-semibold hover:bg-[#1a3266] transition-colors" data-testid="open-map-picker-btn"><MapIcon size={16} /> Apri Google Maps</button>}
      </div>
      {mapOpen && <MapDialog apiKey={key} onClose={() => setMapOpen(false)} onPick={(addr) => { onChange({ ...addr, source: "places" }); setMapKey((n) => n + 1); setMapOpen(false); }} />}
      {key === null && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="maps-not-configured">
          <MapPin size={14} /> Google Maps non configurato: compila l'indirizzo manualmente.
        </div>
      )}
      <Field label="Indirizzo completo / Link Google Maps">{street}</Field>
    </div>
  );
}
